import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Eye } from "lucide-react";
import { toast } from "sonner";
import { CategoryBadge, fmtPT } from "./shared";

export function SuppressionDetailDialog({ recordId, onClose }) {
    const [doc, setDoc] = useState(null);
    const [phone, setPhone] = useState(null);
    const [revealing, setRevealing] = useState(false);

    useEffect(() => {
        setDoc(null);
        setPhone(null);
        if (!recordId) return;
        api.get(`/reports/sms-suppression/history/${recordId}`)
            .then(({ data }) => setDoc(data))
            .catch((e) => toast.error(formatApiError(e)));
    }, [recordId]);

    const reveal = async () => {
        setRevealing(true);
        try {
            const { data } = await api.post(`/reports/sms-suppression/history/${recordId}/reveal`);
            setPhone(data.phone);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRevealing(false);
        }
    };

    return (
        <Dialog open={!!recordId} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="suppression-detail-dialog">
                <DialogHeader><DialogTitle>Suppression record</DialogTitle></DialogHeader>
                {!doc ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                    <div className="space-y-4 text-sm">
                        <div className="flex items-center gap-3 flex-wrap">
                            <span className="font-mono text-base" data-testid="detail-phone">{phone || doc.phone_masked}</span>
                            {!phone && (
                                <Button size="sm" variant="outline" onClick={reveal} disabled={revealing} data-testid="detail-reveal-btn">
                                    <Eye className="h-4 w-4 mr-1" />Reveal full number
                                </Button>
                            )}
                            <CategoryBadge category={doc.category} />
                            <span className="text-xs text-muted-foreground" data-testid="detail-blocks">
                                {doc.blocks_sends ? "Blocks marketing sends" : "Review only, sends allowed"}
                            </span>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-xs">
                            <Field label="First seen (evidence)" value={fmtPT(doc.first_seen_at)} />
                            <Field label="Last seen" value={fmtPT(doc.last_seen_at)} />
                            <Field label={`Record created (${doc.created_by})`} value={fmtPT(doc.record_created_at)} />
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                                Evidence ({doc.evidence_count}, newest 25 shown)
                            </div>
                            <div className="max-h-64 overflow-y-auto border rounded-lg divide-y" data-testid="detail-evidence">
                                {doc.evidence.map((e, i) => (
                                    <div key={i} className="px-3 py-2 text-xs flex flex-wrap gap-x-4">
                                        <span>{fmtPT(e.at)}</span><span className="font-medium">{e.reason}</span>
                                        <span className="text-muted-foreground">{e.source}</span>
                                        {e.error_code ? <span className="font-mono">code {e.error_code}</span> : null}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, value }) {
    return (
        <div className="rounded-lg border px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="mt-0.5">{value}</div>
        </div>
    );
}
